import React, {
  createContext,
  Dispatch,
  FC,
  MutableRefObject,
  SetStateAction,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLoaderTimer } from "@app/neva/hooks";
import { ConfigContext, TestDetailsContext, UserContext } from "@app/provider";
import { testTypeToBotId } from "@app/neva/helpers";
import {
  NativeTestType,
  TestStatus,
  TestType,
} from "@app/provider/testData/types";
import { getNevaPreSessionToken } from "@app/neva/helpers/getNevaPreSessionToken";
import { getBusinessRuleEngineType } from "@app/neva/helpers/getBusinessRuleEngineType";

type NevaProviderProps = {
  children: React.ReactNode;
  hideIfResultViewed: boolean;
};

export interface NevaController {
  startDate: Date;
  botId: string;
  nevaPreSessionToken: string;
  isNevaDisabled: boolean;
  isFirst: boolean;
  pcop: string;
  isWidgetOpen: boolean;
  completeNevaFlow: () => void;
  onReady: () => void;
  setIsWidgetOpen: Dispatch<SetStateAction<boolean>>;
  patientUid?: string;
  patientOktaUid?: string;
  documentUid?: number;
  orderUidPublic?: string;
  isWistiaEnabled: boolean;
  hasVideoRendered: MutableRefObject<boolean>;
  setHasVideoRendered: (value: boolean) => void;
  breType: string | undefined;
  sosLineItemUid?: string;
  limsCaseId?: number;
  nativeTestType?: NativeTestType;
}

export const Context = createContext<NevaController>({
  startDate: new Date(),
  botId: "",
  nevaPreSessionToken: "",
  isNevaDisabled: true,
  isFirst: false,
  pcop: "",
  isWidgetOpen: false,
  completeNevaFlow: () => undefined,
  onReady: () => undefined,
  setIsWidgetOpen: () => undefined,
  patientUid: "",
  patientOktaUid: "",
  documentUid: undefined,
  orderUidPublic: "",
  isWistiaEnabled: false,
  hasVideoRendered: { current: false },
  setHasVideoRendered: () => undefined,
  breType: undefined,
  sosLineItemUid: undefined,
  limsCaseId: undefined,
  nativeTestType: undefined,
});

Context.displayName = "NevaContext";

const NevaProvider: FC<NevaProviderProps> = ({
  children,
  hideIfResultViewed,
}) => {
  const startDate = useRef(new Date());
  const {
    config: {
      wistia: { enabled: isWistiaEnabled },
      nevaConfig: {
        enabled: isNevaGloballyEnabled,
        isAutoExpandEnabled,
        horizonCS3Enabled,
        horizonSOPLEnabled,
        horizonXEnabled,
        panoramaEnabled,
        empowerEnabled,
      },
    },
  } = useContext(ConfigContext);
  const { isLoading, getTestDetails, refetchTest } =
    useContext(TestDetailsContext);
  const { uppUser } = useContext(UserContext);

  const [hasCompletedNevaFlow, setHasCompletedNevaFlow] = useState(false);
  const [isWidgetOpen, setIsWidgetOpen] = useState(false);
  let testCard = getTestDetails();
  const hasViewedResult = testCard?.viewStatus === TestStatus.VIEWED;
  const autoExpand = isAutoExpandEnabled && !hasViewedResult;
  const isFirst = !hasCompletedNevaFlow && !hasViewedResult;
  //define logic
  const pcop = "";
  const botId = testTypeToBotId(testCard?.testType);
  const nevaPreSessionToken = getNevaPreSessionToken(testCard);
  const patientUid = uppUser?.patientUID;
  const patientOktaUid = uppUser?.oktaUID;
  const documentUid = testCard?.resultDocumentsInfo.length
    ? testCard.resultDocumentsInfo[testCard.resultDocumentsInfo.length - 1]
        .documentReferenceId
    : undefined;
  const orderUidPublic = testCard?.orderUidPublic;
  const hasVideoRendered = useRef(false);
  const breType = getBusinessRuleEngineType(
    testCard?.testType,
    testCard?.nevaSessionToken
  );
  const sosLineItemUid = testCard?.uid;
  const limsCaseId = testCard?.limsId;
  const isNevaDisabled =
    !isNevaGloballyEnabled ||
    isLoading ||
    !testCard?.isNevaEnabled ||
    (hideIfResultViewed && (hasCompletedNevaFlow || hasViewedResult)) ||
    !(
      (testCard.testType === TestType.HORIZON &&
        testCard.nativeTestType === NativeTestType.CARRIER_SCREENING_3 &&
        horizonCS3Enabled) ||
      (testCard.testType === TestType.HORIZON &&
        testCard.nativeTestType === NativeTestType.CARRIER_SCREENING_SOPL &&
        horizonSOPLEnabled) ||
      (testCard.testType === TestType.HORIZON &&
        testCard.nativeTestType === NativeTestType.CARRIER_SCREENING_X &&
        horizonXEnabled) ||
      (testCard.testType === TestType.PANORAMA && panoramaEnabled) ||
      (testCard.testType === TestType.EMPOWER && empowerEnabled)
    );
  const nativeTestType =
    testCard?.testType === TestType.HORIZON
      ? testCard.nativeTestType
      : undefined;

  useEffect(() => {
    if (!isNevaDisabled) {
      startLoaderTimer();
    }
  }, [isNevaDisabled]);

  useEffect(() => {
    if (autoExpand) {
      setIsWidgetOpen(true);
    }
  }, [autoExpand]);

  const setHasVideoRendered = (value: boolean) => {
    hasVideoRendered.current = value;
  };

  const handleNevaTimeout = () => {
    setIsWidgetOpen(false);
    if (!hasViewedResult) {
      completeNevaFlow();
    }
  };

  const { startLoaderTimer, nevaOnline } = useLoaderTimer(handleNevaTimeout);

  const completeNevaFlow = () => {
    setHasCompletedNevaFlow(true);
    refetchTest().then((testDetail) => {
      testCard = testDetail;
    });
  };

  const value = useMemo(
    () => ({
      startDate: startDate.current,
      botId,
      nevaPreSessionToken,
      isNevaDisabled,
      isFirst,
      pcop,
      isWidgetOpen,
      completeNevaFlow,
      onReady: nevaOnline,
      setIsWidgetOpen,
      patientUid,
      patientOktaUid,
      documentUid,
      orderUidPublic,
      isWistiaEnabled,
      hasVideoRendered,
      setHasVideoRendered,
      breType,
      sosLineItemUid,
      limsCaseId,
      nativeTestType,
    }),
    [
      startDate,
      botId,
      nevaPreSessionToken,
      isNevaDisabled,
      isFirst,
      pcop,
      isWidgetOpen,
      completeNevaFlow,
      nevaOnline,
      setIsWidgetOpen,
      patientUid,
      patientOktaUid,
      documentUid,
      orderUidPublic,
      isWistiaEnabled,
      hasVideoRendered,
      breType,
      sosLineItemUid,
      limsCaseId,
      nativeTestType,
    ]
  );

  return <Context.Provider value={value}>{children}</Context.Provider>;
};

export default NevaProvider;
